import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { ThemeProvider, createTheme, responsiveFontSizes } from '@mui/material/styles'
import { useMatch } from 'react-router-dom'
import kinTheme from '../config/theme'
import { createProjectTheme, defaultProjectId, projectThemes } from '../config/projectThemes'

const ProjectThemeContext = createContext(null)
const storageKey = 'kin-selected-project'
const baseTheme = responsiveFontSizes(createTheme(kinTheme))
const isProject = (projectId) => Object.prototype.hasOwnProperty.call(projectThemes, projectId)

const getSavedProject = () => {
    try {
        const savedProject = window.localStorage.getItem(storageKey)
        if (isProject(savedProject)) return savedProject
    } catch {
        // Selection still works when browser storage is unavailable.
    }

    return defaultProjectId
}

export const ProjectThemeProvider = ({ children }) => {
    const [selectedProjectId, setSelectedProjectId] = useState(getSavedProject)
    // Extend this route scope when project themes should apply to the whole site.
    const projectThemeEnabled = Boolean(useMatch('/portafolio'))

    const selectProject = useCallback((projectId) => {
        if (isProject(projectId)) setSelectedProjectId(projectId)
    }, [])

    useEffect(() => {
        try {
            window.localStorage.setItem(storageKey, selectedProjectId)
        } catch {
            // Keep the in-memory selection if storage is blocked or full.
        }
    }, [selectedProjectId])

    const theme = useMemo(() => (
        projectThemeEnabled ? createProjectTheme(selectedProjectId) : baseTheme
    ), [projectThemeEnabled, selectedProjectId])

    const value = useMemo(() => ({ selectedProjectId, selectProject }), [selectedProjectId, selectProject])

    return (
        <ProjectThemeContext.Provider value={value}>
            <ThemeProvider theme={theme}>{children}</ThemeProvider>
        </ProjectThemeContext.Provider>
    )
}

export const useProjectTheme = () => {
    const context = useContext(ProjectThemeContext)
    if (!context) throw new Error('useProjectTheme must be used within ProjectThemeProvider')
    return context
}
